import { useState, useMemo } from 'react';
import { Modal, Button } from '../ui';
import { Collection } from '../../api/collections';
import { randomCollectionColor } from '../../utils/formatTime';

const COLLECTION_COLORS = [
  '#C45C26', // Orange
  '#7C9A6E', // Green
  '#D4A853', // Yellow/Gold
  '#5B8DB8', // Blue
  '#9B6B9E', // Purple
  '#C97B63', // Coral
  '#6B9B9A', // Teal
  '#B8A05B', // Olive
];

interface CollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; color: string }) => void;
  collection?: Collection | null;
  isLoading?: boolean;
}

export function CollectionModal({ isOpen, onClose, onSave, collection, isLoading }: CollectionModalProps) {
  // Generate a stable random color for new collections per modal open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultColor = useMemo(() => randomCollectionColor(), [isOpen]);

  // Use lazy initialization to set initial values from props
  const [name, setName] = useState(() => collection?.name ?? '');
  const [color, setColor] = useState(() => collection?.color ?? randomCollectionColor());

  // Sync state when modal opens or collection changes (for subsequent renders)
  const key = `${isOpen}-${collection?.id ?? 'new'}`;
  const [prevKey, setPrevKey] = useState(key);
  if (key !== prevKey) {
    setPrevKey(key);
    setName(collection?.name ?? '');
    setColor(collection?.color ?? defaultColor);
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={collection ? 'Edit Collection' : 'New Collection'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isLoading} disabled={!name.trim()}>
            {collection ? 'Save Changes' : 'Create Collection'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="collection-name" className="form-label required">
            Collection Name
          </label>
          <input
            type="text"
            id="collection-name"
            className="form-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter collection name"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label">Color</label>
          <div
            className="color-options"
            style={{
              display: 'flex',
              gap: 'var(--spacing-sm)',
              flexWrap: 'wrap',
            }}
          >
            {COLLECTION_COLORS.map(c => (
              <button
                key={c}
                type="button"
                className={`color-option ${color === c ? 'selected' : ''}`}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: c,
                  border: color === c ? '3px solid var(--color-primary)' : '2px solid transparent',
                  cursor: 'pointer',
                  outline: 'none',
                }}
                onClick={() => setColor(c)}
                aria-label={`Select color ${c}`}
              />
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
